"use client";

import React, { InputHTMLAttributes, useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { cn } from "../utils";

interface FormInputProps extends InputHTMLAttributes<HTMLInputElement> {
    label: string;
    error?: string;
    icon?: React.ReactNode;
    helperText?: string;
}

export const FormInput = React.forwardRef<HTMLInputElement, FormInputProps>(
    ({ label, error, icon, helperText, className, type, ...props }, ref) => {
        const [showPassword, setShowPassword] = useState(false);
        const isPassword = type === "password";
        const resolvedType = isPassword && showPassword ? "text" : type;

        const iconElement = React.isValidElement<{ className?: string }>(icon)
            ? React.cloneElement(icon, {
                  className: cn("h-4 w-4", icon.props.className),
              })
            : icon;

        return (
            <div className="space-y-2 w-full">
                <label className="block text-xs font-bold text-muted-foreground uppercase tracking-widest pl-1">
                    {label}
                    {props.required && <span className="text-destructive ml-1">*</span>}
                </label>
                <div className="relative group">
                    {icon && (
                        <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground group-focus-within:text-primary transition-colors pointer-events-none">
                            {iconElement}
                        </div>
                    )}
                    <input
                        ref={ref}
                        type={resolvedType}
                        className={cn(
                            "block w-full h-12 py-3 border rounded-xl text-base sm:text-sm font-medium outline-none transition-all bg-muted/20",
                            icon ? "pl-11" : "px-4",
                            isPassword ? (icon ? "pr-11" : "pr-11") : (icon ? "pr-4" : ""),
                            error
                                ? "border-destructive/30 focus:ring-4 focus:ring-destructive/5 focus:border-destructive/60"
                                : "border-border/60 focus:ring-4 focus:ring-primary/5 focus:border-primary/40 focus:bg-background",
                            className
                        )}
                        inputMode={type === "number" ? "decimal" : props.inputMode}
                        {...props}
                    />
                    {isPassword && (
                        <button
                            type="button"
                            tabIndex={-1}
                            onClick={() => setShowPassword((v) => !v)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-lg text-muted-foreground hover:text-foreground transition-colors"
                            aria-label={showPassword ? "Hide password" : "Show password"}
                        >
                            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                    )}
                </div>
                {error && <p className="text-[10px] font-bold text-destructive mt-1 uppercase tracking-wider pl-1">{error}</p>}
                {!error && helperText && <p className="text-[10px] font-medium text-muted-foreground mt-1 pl-1">{helperText}</p>}
            </div>
        );
    }
);

FormInput.displayName = "FormInput";
